import {
  type CanvasElementDefinition,
  type MigrationRegistry,
} from '../canvas-elements/element-definition';

export function validateElementDefinition(elementDefinition: CanvasElementDefinition): void {
  const { type, version, migrations } = elementDefinition;
  validateElementIoConfig(elementDefinition);
  validateElementMigrations(type, version, migrations);
}

export function validateElementIoConfig(elementDefinition: CanvasElementDefinition): void {
  if ((elementDefinition.ioConfig?.inputs?.filter((input) => input.isQueryable).length ?? 0) > 1) {
    throw new Error('isQueryable is not supported for more than one input');
  }
}

export function validateElementMigrations(
  type: string,
  version: number | undefined,
  migrations: MigrationRegistry | undefined,
): void {
  if (!migrations || Object.keys(migrations).length === 0) {
    return;
  }

  if (!version) {
    throw new Error(
      `Invalid migrations for element type '${type}': version must be defined when migrations are provided`,
    );
  }

  if (!Number.isInteger(version) || version < 2) {
    throw new Error(
      `Invalid migrations for element type '${type}': version must be an integer >= 2 when migrations are provided (got ${version})`,
    );
  }

  const migrationVersions = Object.keys(migrations).map((key) => {
    const parsed = Number(key);
    if (!Number.isInteger(parsed)) {
      throw new Error(
        `Invalid migrations for element type '${type}': migration key '${key}' is not a valid integer version`,
      );
    }
    return parsed;
  });

  const sortedVersions = [...new Set(migrationVersions)].sort((a, b) => a - b);

  if (sortedVersions.length === 0) {
    return;
  }

  const expectedStart = 2;
  const expectedEnd = version;

  if (sortedVersions[0] !== expectedStart) {
    throw new Error(
      `Invalid migrations for element type '${type}': migrations must start from version ${expectedStart} (got ${sortedVersions[0]})`,
    );
  }

  if (sortedVersions[sortedVersions.length - 1] !== expectedEnd) {
    throw new Error(
      `Invalid migrations for element type '${type}': last migration must target current version ${expectedEnd} (got ${sortedVersions[sortedVersions.length - 1]})`,
    );
  }

  for (let i = 0; i < sortedVersions.length; i++) {
    const expectedVersion = expectedStart + i;
    const actualVersion = sortedVersions[i];
    if (actualVersion !== expectedVersion) {
      throw new Error(
        `Invalid migrations for element type '${type}': missing migration to version ${expectedVersion}. Defined migrations: [${sortedVersions.join(
          ', ',
        )}]`,
      );
    }
  }
}
